/**
 * Tells the user when an analysis starts running and when it completes or
 * fails — wherever they are in the app. Mounted once, beside the toasts.
 *
 * Watches every audit in `runWatch` (added when a run is started, or when a
 * page finds one in progress): re-reads its analyses every few seconds and
 * compares each analysis's status with the last reading. Several changing at
 * once (a "Re-run all") make one summary toast, not one each. An audit is let
 * go once nothing of it is running.
 */
import { useEffect, useRef } from 'react';
import { useQueries } from '@tanstack/react-query';
import { analysisOverviewQueryOptions } from '../queries/analysis';
import { useAppDispatch, useAppSelector } from '../store';
import { unwatchAudit } from '../store/runWatchSlice';
import { useToast } from '../provider/toast';
import {
	analysisRows,
	type AnalysisRow,
} from '../features/analyses/analysisRows';
import { count } from '../utils/format';

type Phase = 'RUNNING' | 'COMPLETED' | 'FAILED' | 'OTHER';

const phaseOf = (status: string): Phase =>
	status === 'RUNNING' || status === 'QUEUED'
		? 'RUNNING'
		: status === 'COMPLETED'
			? 'COMPLETED'
			: status === 'FAILED'
				? 'FAILED'
				: 'OTHER';

/** More changes than this in one reading become one summary toast. */
const ONE_BY_ONE = 2;
/** A run the server never shows as started is let go after this long. */
const GIVE_UP_MS = 60_000;

const nameOf = (entry: AnalysisRow) => `${entry.number}. ${entry.title}`;

export function AnalysisRunWatcher() {
	const watched = useAppSelector((state) => state.runWatch.audits);
	const auditIds = Object.keys(watched);
	const dispatch = useAppDispatch();
	const toast = useToast();

	const results = useQueries({
		queries: auditIds.map((auditId) => ({
			...analysisOverviewQueryOptions(auditId),
			refetchInterval: 3000,
		})),
	});

	/** Per audit: each analysis's phase at the last reading, and whether a run
	 *  has been seen at all since watching began. */
	const seen = useRef(
		new Map<string, { phases: Map<number, Phase>; sawRunning: boolean }>(),
	);

	const readings = results.map((result) => result.dataUpdatedAt).join(',');
	useEffect(() => {
		auditIds.forEach((auditId, index) => {
			const data = results[index]?.data;
			const watch = watched[auditId];
			if (!data || !watch) return;
			const entries = analysisRows(data.rows, data.analyses).filter(
				(entry) => entry.rollup,
			);
			const phases = new Map(
				entries.map((entry) => [
					entry.number,
					phaseOf(entry.rollup?.status ?? ''),
				]),
			);
			const previous = seen.current.get(auditId);
			const started: AnalysisRow[] = [];
			const completed: AnalysisRow[] = [];
			const failed: AnalysisRow[] = [];
			for (const entry of entries) {
				const now = phases.get(entry.number);
				const before = previous?.phases.get(entry.number);
				if (now === 'RUNNING' && before !== 'RUNNING') {
					if (previous || watch.announceStart) started.push(entry);
				} else if (before === 'RUNNING' && now === 'COMPLETED')
					completed.push(entry);
				else if (before === 'RUNNING' && now === 'FAILED')
					failed.push(entry);
			}

			if (started.length > ONE_BY_ONE) {
				toast(
					'info',
					`${count(started.length)} analyses started running`,
				);
			} else {
				for (const entry of started)
					toast('info', `${nameOf(entry)} started running`);
			}
			if (completed.length > ONE_BY_ONE) {
				toast('ok', `${count(completed.length)} analyses completed`);
			} else {
				for (const entry of completed) {
					const findings = entry.rollup?.exceptionFindings;
					toast(
						'ok',
						`${nameOf(entry)} completed`,
						findings === null || findings === undefined
							? undefined
							: findings === 0
								? 'No findings.'
								: `${count(findings)} findings.`,
					);
				}
			}
			if (failed.length > ONE_BY_ONE) {
				toast('danger', `${count(failed.length)} analyses failed`);
			} else {
				for (const entry of failed) {
					toast(
						'danger',
						`${nameOf(entry)} failed`,
						entry.rollup?.error ?? undefined,
					);
				}
			}

			const running = [...phases.values()].includes('RUNNING');
			const sawRunning = Boolean(previous?.sawRunning) || running;
			seen.current.set(auditId, { phases, sawRunning });
			const tooLong = Date.now() - watch.since > GIVE_UP_MS;
			if (!running && (sawRunning || tooLong)) {
				seen.current.delete(auditId);
				dispatch(unwatchAudit(auditId));
			}
		});
		// `readings` changes exactly when a new reading arrives.
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [readings]);

	return null;
}
